import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Users, Settings, ClipboardCheck, UtensilsCrossed, BarChart3, AlertTriangle } from 'lucide-react';
import { api } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import ConfirmDialog from './ConfirmDialog';
import { Toast } from './ui/Toast';
import { LongToast, shouldUseLongToast } from './ui/LongToast';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';

import CantineHeader from './cantine/CantineHeader';
import CantineParametresSection from './cantine/CantineParametresSection';
import CantineInscriptionsSection from './cantine/CantineInscriptionsSection';
import CantinePointageSection from './cantine/CantinePointageSection';
import CantineMenusSection from './cantine/CantineMenusSection';
import CantineTableauBordSection from './cantine/CantineTableauBordSection';
import CantineCreancesSection from './cantine/CantineCreancesSection';
import ModalInscriptionCantine from './cantine/modals/ModalInscriptionCantine';
import ModalRestrictionsEleve from './cantine/modals/ModalRestrictionsEleve';
import ModalMenuJour from './cantine/modals/ModalMenuJour';
import { formatDateForAPI, getLundiDeLaSemaine, getJoursDeLaSemaine } from './cantine/cantineUtils';

const MENU_VIDE = { entree: '', plat: '', dessert: '', observations: '' };

const JOURS_SERVICE_DEFAUT = [1, 2, 3, 4, 5];

const INSCRIPTION_VIDE = {
  eleve_id: '',
  regime_facturation: 'forfait',
  jours_semaine: [],
  date_debut: '',
  date_fin: '',
  statut: 'actif',
  observations: ''
};

const CantinePage = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [anneeActive, setAnneeActive] = useState(null);
  const [activeTab, setActiveTab] = useState('pointage');

  // Pointage du jour
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [feuille, setFeuille] = useState(null);
  const [loadingFeuille, setLoadingFeuille] = useState(true);
  const [isSavingPointage, setIsSavingPointage] = useState(false);
  const [filterSectionPointage, setFilterSectionPointage] = useState('');
  const [filterClassePointage, setFilterClassePointage] = useState('');

  // Menus
  const [lundiSemaine, setLundiSemaine] = useState(() => getLundiDeLaSemaine(new Date()));
  const [menusParDate, setMenusParDate] = useState({});
  const [loadingMenus, setLoadingMenus] = useState(true);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [menuDate, setMenuDate] = useState(null);
  const [menuForm, setMenuForm] = useState(MENU_VIDE);
  const [isSavingMenu, setIsSavingMenu] = useState(false);
  const [confirmSuppressionMenu, setConfirmSuppressionMenu] = useState(null);

  // Tableau de bord
  const [periodeTdb, setPeriodeTdb] = useState('mois');
  const [tableauBord, setTableauBord] = useState(null);
  const [loadingTableauBord, setLoadingTableauBord] = useState(true);

  // Créances (repas non couverts)
  const [creances, setCreances] = useState(null);
  const [loadingCreances, setLoadingCreances] = useState(true);
  const [isSyncingInscriptions, setIsSyncingInscriptions] = useState(false);

  // Paramètres
  const [loadingParametres, setLoadingParametres] = useState(true);
  const [isSavingParametres, setIsSavingParametres] = useState(false);
  const [joursService, setJoursService] = useState(JOURS_SERVICE_DEFAUT);
  const [autoriserRepasNonPaye, setAutoriserRepasNonPaye] = useState(false);
  const [observations, setObservations] = useState('');
  const [referenceParametres, setReferenceParametres] = useState(null);
  const [expandedSections, setExpandedSections] = useState(new Set(['parametres']));

  // Inscriptions
  const [loadingInscriptions, setLoadingInscriptions] = useState(true);
  const [inscriptions, setInscriptions] = useState([]);
  const [resumeInscriptions, setResumeInscriptions] = useState({
    total: 0, forfait: 0, occasionnel: 0, actifs: 0, avec_restriction_critique: 0
  });
  const [sections, setSections] = useState([]);
  const [classes, setClasses] = useState([]);
  const [eleves, setEleves] = useState([]);

  const [filterStatut, setFilterStatut] = useState('all');
  const [filterRegime, setFilterRegime] = useState('all');
  const [filterSection, setFilterSection] = useState('');
  const [filterClasse, setFilterClasse] = useState('');
  const [search, setSearch] = useState('');

  // Modales
  const [showInscriptionModal, setShowInscriptionModal] = useState(false);
  const [inscriptionMode, setInscriptionMode] = useState('create');
  const [inscriptionForm, setInscriptionForm] = useState(INSCRIPTION_VIDE);
  const [isSavingInscription, setIsSavingInscription] = useState(false);
  const [confirmSuppression, setConfirmSuppression] = useState(null);

  const [showRestrictionsModal, setShowRestrictionsModal] = useState(false);
  const [eleveRestrictions, setEleveRestrictions] = useState(null);
  const [restrictions, setRestrictions] = useState([]);
  const [loadingRestrictions, setLoadingRestrictions] = useState(false);
  const [isSavingRestriction, setIsSavingRestriction] = useState(false);

  const [toastInfo, setToastInfoState] = useState(null);

  const showToast = (message, type = 'success', options = {}) => {
    if (!message) return;
    setToastInfoState({ message, type, ...options });
  };
  const hideToast = () => setToastInfoState(null);

  const toggleSection = (sectionName) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionName)) newSet.delete(sectionName); else newSet.add(sectionName);
      return newSet;
    });
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    setCurrentUser(user);
  }, []);

  useEffect(() => {
    const fetchAnneeActive = async () => {
      try {
        const response = await api.anneesScolaires.getActive();
        if (response.success && response.data) setAnneeActive(response.data);
      } catch (error) {
        console.error("Erreur lors de la récupération de l'année scolaire active:", error);
      }
    };
    fetchAnneeActive();
  }, []);

  const anneeScolaireId = anneeActive?.id || localStorage.getItem('annee_scolaire_id');

  // ==========================================================================
  // PARAMETRES
  // ==========================================================================
  const applyParametres = useCallback((data) => {
    const jours = Array.isArray(data?.jours_service) && data.jours_service.length
      ? data.jours_service
      : JOURS_SERVICE_DEFAUT;
    setJoursService(jours);
    setAutoriserRepasNonPaye(Boolean(data?.autoriser_repas_non_paye));
    setObservations(data?.observations || '');
    setReferenceParametres({
      jours_service: jours,
      autoriser_repas_non_paye: Boolean(data?.autoriser_repas_non_paye),
      observations: data?.observations || ''
    });
  }, []);

  const loadParametres = useCallback(async () => {
    if (!anneeScolaireId) return;
    try {
      setLoadingParametres(true);
      const response = await api.cantine.getParametres({ annee_scolaire_id: anneeScolaireId });
      if (response.success) applyParametres(response.data);
      else showToast(response.message || 'Erreur lors du chargement des paramètres.', 'error');
    } catch (error) {
      console.error('Erreur chargement paramètres cantine:', error);
      showToast('Erreur lors du chargement des paramètres de la cantine.', 'error');
    } finally {
      setLoadingParametres(false);
    }
  }, [anneeScolaireId, applyParametres]);

  const toggleJour = (jour) => {
    setJoursService((prev) => (
      prev.includes(jour) ? prev.filter((v) => v !== jour) : [...prev, jour].sort((a, b) => a - b)
    ));
  };

  const isParametresDirty = useMemo(() => {
    if (!referenceParametres) return false;
    return (
      referenceParametres.jours_service.join(',') !== joursService.join(',') ||
      referenceParametres.autoriser_repas_non_paye !== autoriserRepasNonPaye ||
      (referenceParametres.observations || '') !== (observations || '')
    );
  }, [referenceParametres, joursService, autoriserRepasNonPaye, observations]);

  const handleSaveParametres = async () => {
    if (joursService.length === 0) {
      showToast('Sélectionnez au moins un jour de service.', 'error');
      return;
    }
    try {
      setIsSavingParametres(true);
      const response = await api.cantine.saveParametres({
        annee_scolaire_id: anneeScolaireId,
        jours_service: joursService.join(','),
        autoriser_repas_non_paye: autoriserRepasNonPaye,
        observations
      });
      if (response.success) {
        applyParametres(response.data);
        showToast('Paramètres de la cantine enregistrés.', 'success');
      } else {
        showToast(response.message || "Erreur lors de l'enregistrement des paramètres.", 'error');
      }
    } catch (error) {
      console.error('Erreur enregistrement paramètres cantine:', error);
      showToast("Erreur lors de l'enregistrement des paramètres.", 'error');
    } finally {
      setIsSavingParametres(false);
    }
  };

  // ==========================================================================
  // REFERENTIELS (sections, classes, élèves)
  // ==========================================================================
  useEffect(() => {
    const loadReferentiels = async () => {
      try {
        const sectionsResponse = await api.sections.getAll();
        if (sectionsResponse.success) setSections(sectionsResponse.data || []);

        const classesData = await api.classes.getAll();
        if (Array.isArray(classesData)) setClasses(classesData);
      } catch (error) {
        console.error('Erreur chargement sections/classes:', error);
      }
    };
    loadReferentiels();
  }, []);

  useEffect(() => {
    const loadEleves = async () => {
      if (!anneeScolaireId) return;
      try {
        const response = await api.eleves.getAll(anneeScolaireId);
        const liste = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        setEleves(liste);
      } catch (error) {
        console.error('Erreur chargement élèves:', error);
      }
    };
    loadEleves();
  }, [anneeScolaireId]);

  // ==========================================================================
  // INSCRIPTIONS
  // ==========================================================================
  const loadInscriptions = useCallback(async () => {
    if (!anneeScolaireId) return;
    try {
      setLoadingInscriptions(true);
      const response = await api.cantine.getInscriptions({
        annee_scolaire_id: anneeScolaireId,
        statut: filterStatut,
        regime: filterRegime,
        section_id: filterSection,
        classe_id: filterClasse,
        search
      });
      if (response.success) {
        setInscriptions(response.data.inscriptions || []);
        setResumeInscriptions(response.data.resume || {});
      } else {
        showToast(response.message || 'Erreur lors du chargement des inscriptions.', 'error');
      }
    } catch (error) {
      console.error('Erreur chargement inscriptions cantine:', error);
      showToast('Erreur lors du chargement des inscriptions.', 'error');
    } finally {
      setLoadingInscriptions(false);
    }
  }, [anneeScolaireId, filterStatut, filterRegime, filterSection, filterClasse, search]);

  useEffect(() => {
    if (anneeScolaireId) loadParametres();
  }, [anneeScolaireId, loadParametres]);

  useEffect(() => {
    if (anneeScolaireId) loadInscriptions();
  }, [anneeScolaireId, loadInscriptions]);

  const handleSectionChange = (sectionId) => {
    setFilterSection(sectionId);
    setFilterClasse('');
  };

  const ouvrirNouvelleInscription = () => {
    setInscriptionMode('create');
    setInscriptionForm(INSCRIPTION_VIDE);
    setShowInscriptionModal(true);
  };

  const ouvrirModificationInscription = (inscription) => {
    setInscriptionMode('edit');
    setInscriptionForm({
      eleve_id: inscription.eleve_id,
      eleve_nom: inscription.nom,
      eleve_prenom: inscription.prenom,
      classe_nom: inscription.classe_nom,
      matricule: inscription.matricule,
      regime_facturation: inscription.regime_facturation,
      jours_semaine: inscription.jours_semaine || [],
      date_debut: inscription.date_debut ? String(inscription.date_debut).slice(0, 10) : '',
      date_fin: inscription.date_fin ? String(inscription.date_fin).slice(0, 10) : '',
      statut: inscription.statut,
      observations: inscription.observations || ''
    });
    setShowInscriptionModal(true);
  };

  const handleSaveInscription = async () => {
    if (!inscriptionForm.eleve_id) {
      showToast('Sélectionnez un élève.', 'error');
      return;
    }
    try {
      setIsSavingInscription(true);
      const response = await api.cantine.saveInscription({
        annee_scolaire_id: anneeScolaireId,
        eleve_id: inscriptionForm.eleve_id,
        regime_facturation: inscriptionForm.regime_facturation,
        jours_semaine: (inscriptionForm.jours_semaine || []).join(','),
        date_debut: inscriptionForm.date_debut || null,
        date_fin: inscriptionForm.date_fin || null,
        statut: inscriptionForm.statut,
        observations: inscriptionForm.observations
      });

      if (response.success) {
        showToast(
          inscriptionMode === 'edit' ? 'Inscription mise à jour.' : "Élève inscrit à la cantine.",
          'success'
        );
        setShowInscriptionModal(false);
        await loadInscriptions();
      } else {
        showToast(response.message || "Erreur lors de l'enregistrement de l'inscription.", 'error');
      }
    } catch (error) {
      console.error('Erreur enregistrement inscription cantine:', error);
      showToast("Erreur lors de l'enregistrement de l'inscription.", 'error');
    } finally {
      setIsSavingInscription(false);
    }
  };

  const confirmerSuppressionInscription = async () => {
    if (!confirmSuppression) return;
    try {
      setIsSavingInscription(true);
      const response = await api.cantine.deleteInscription(confirmSuppression.id, {
        annee_scolaire_id: anneeScolaireId
      });
      if (response.success) {
        showToast('Élève retiré de la cantine.', 'success');
        await loadInscriptions();
      } else {
        showToast(response.message || 'Erreur lors de la suppression.', 'error');
      }
    } catch (error) {
      console.error('Erreur suppression inscription cantine:', error);
      showToast('Erreur lors de la suppression.', 'error');
    } finally {
      setIsSavingInscription(false);
      setConfirmSuppression(null);
    }
  };

  // ==========================================================================
  // RESTRICTIONS ALIMENTAIRES
  // ==========================================================================
  const ouvrirRestrictions = async (inscription) => {
    setEleveRestrictions(inscription);
    setShowRestrictionsModal(true);
    setRestrictions([]);
    try {
      setLoadingRestrictions(true);
      const response = await api.cantine.getRestrictions({
        annee_scolaire_id: anneeScolaireId,
        eleve_id: inscription.eleve_id
      });
      if (response.success) setRestrictions(response.data.restrictions || []);
      else showToast(response.message || 'Erreur lors du chargement des restrictions.', 'error');
    } catch (error) {
      console.error('Erreur chargement restrictions:', error);
      showToast('Erreur lors du chargement des restrictions.', 'error');
    } finally {
      setLoadingRestrictions(false);
    }
  };

  const rechargerRestrictions = async () => {
    if (!eleveRestrictions) return;
    const response = await api.cantine.getRestrictions({
      annee_scolaire_id: anneeScolaireId,
      eleve_id: eleveRestrictions.eleve_id
    });
    if (response.success) setRestrictions(response.data.restrictions || []);
  };

  const handleAjouterRestriction = async (form) => {
    if (!form.libelle.trim()) {
      showToast('Le libellé de la restriction est requis.', 'error');
      return false;
    }
    try {
      setIsSavingRestriction(true);
      const response = await api.cantine.saveRestriction({
        annee_scolaire_id: anneeScolaireId,
        eleve_id: eleveRestrictions.eleve_id,
        type: form.type,
        libelle: form.libelle,
        severite: form.severite,
        observations: form.observations
      });
      if (response.success) {
        showToast('Restriction alimentaire enregistrée.', 'success');
        await rechargerRestrictions();
        await loadInscriptions();
        return true;
      }
      showToast(response.message || "Erreur lors de l'enregistrement.", 'error');
      return false;
    } catch (error) {
      console.error('Erreur enregistrement restriction:', error);
      showToast("Erreur lors de l'enregistrement de la restriction.", 'error');
      return false;
    } finally {
      setIsSavingRestriction(false);
    }
  };

  const handleSupprimerRestriction = async (restriction) => {
    try {
      setIsSavingRestriction(true);
      const response = await api.cantine.deleteRestriction(restriction.id, {
        annee_scolaire_id: anneeScolaireId
      });
      if (response.success) {
        showToast('Restriction supprimée.', 'success');
        await rechargerRestrictions();
        await loadInscriptions();
      } else {
        showToast(response.message || 'Erreur lors de la suppression.', 'error');
      }
    } catch (error) {
      console.error('Erreur suppression restriction:', error);
      showToast('Erreur lors de la suppression de la restriction.', 'error');
    } finally {
      setIsSavingRestriction(false);
    }
  };

  // ==========================================================================
  // POINTAGE DU JOUR
  // ==========================================================================
  const loadFeuilleDuJour = useCallback(async () => {
    if (!anneeScolaireId) return;
    try {
      setLoadingFeuille(true);
      const response = await api.cantine.getFeuilleDuJour({
        annee_scolaire_id: anneeScolaireId,
        date_service: formatDateForAPI(selectedDate),
        section_id: filterSectionPointage,
        classe_id: filterClassePointage
      });
      if (response.success) {
        setFeuille(response.data);
      } else {
        showToast(response.message || 'Erreur lors du chargement de la feuille du jour.', 'error');
      }
    } catch (error) {
      console.error('Erreur chargement feuille du jour:', error);
      showToast('Erreur lors du chargement de la feuille du jour.', 'error');
    } finally {
      setLoadingFeuille(false);
    }
  }, [anneeScolaireId, selectedDate, filterSectionPointage, filterClassePointage]);

  useEffect(() => {
    if (anneeScolaireId) loadFeuilleDuJour();
  }, [anneeScolaireId, loadFeuilleDuJour]);

  const goToPreviousDay = () => {
    setSelectedDate((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() - 1);
      return next;
    });
  };

  const goToNextDay = () => {
    setSelectedDate((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + 1);
      return next;
    });
  };

  const goToToday = () => setSelectedDate(new Date());

  const handleDateChange = (event) => {
    if (!event.target.value) return;
    setSelectedDate(new Date(`${event.target.value}T12:00:00`));
  };

  const handleSectionPointageChange = (sectionId) => {
    setFilterSectionPointage(sectionId);
    setFilterClassePointage('');
  };

  const handlePointer = async (ligne, statut) => {
    try {
      setIsSavingPointage(true);
      const response = await api.cantine.enregistrerPointage({
        annee_scolaire_id: anneeScolaireId,
        eleve_id: ligne.eleve_id,
        date_service: feuille?.date_service,
        statut
      });

      if (response.success) {
        await loadFeuilleDuJour();
      } else {
        // Cas metier principal : un occasionnel non paye alors que la regle l'interdit.
        showToast(response.message || "Erreur lors de l'enregistrement du pointage.", 'error', { long: true });
      }
    } catch (error) {
      console.error('Erreur pointage cantine:', error);
      showToast("Erreur lors de l'enregistrement du pointage.", 'error');
    } finally {
      setIsSavingPointage(false);
    }
  };

  const handleAnnulerPointage = async (ligne) => {
    try {
      setIsSavingPointage(true);
      const response = await api.cantine.annulerPointage({
        annee_scolaire_id: anneeScolaireId,
        eleve_id: ligne.eleve_id,
        date_service: feuille?.date_service
      });

      if (response.success) {
        await loadFeuilleDuJour();
      } else {
        showToast(response.message || "Erreur lors de l'annulation du pointage.", 'error');
      }
    } catch (error) {
      console.error('Erreur annulation pointage cantine:', error);
      showToast("Erreur lors de l'annulation du pointage.", 'error');
    } finally {
      setIsSavingPointage(false);
    }
  };

  // ==========================================================================
  // MENUS
  // ==========================================================================
  const loadMenus = useCallback(async () => {
    if (!anneeScolaireId) return;
    try {
      setLoadingMenus(true);
      const jours = getJoursDeLaSemaine(lundiSemaine);
      const response = await api.cantine.getMenus({
        annee_scolaire_id: anneeScolaireId,
        date_debut: formatDateForAPI(jours[0]),
        date_fin: formatDateForAPI(jours[6])
      });

      if (response.success) {
        const parDate = {};
        (response.data.menus || []).forEach((menu) => {
          parDate[String(menu.date_service).slice(0, 10)] = menu;
        });
        setMenusParDate(parDate);
      } else {
        showToast(response.message || 'Erreur lors du chargement des menus.', 'error');
      }
    } catch (error) {
      console.error('Erreur chargement menus cantine:', error);
      showToast('Erreur lors du chargement des menus.', 'error');
    } finally {
      setLoadingMenus(false);
    }
  }, [anneeScolaireId, lundiSemaine]);

  useEffect(() => {
    if (anneeScolaireId) loadMenus();
  }, [anneeScolaireId, loadMenus]);

  const goToPreviousWeek = () => {
    setLundiSemaine((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() - 7);
      return next;
    });
  };

  const goToNextWeek = () => {
    setLundiSemaine((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + 7);
      return next;
    });
  };

  const goToCurrentWeek = () => setLundiSemaine(getLundiDeLaSemaine(new Date()));

  const ouvrirEditionMenu = (date, menu) => {
    setMenuDate(date);
    setMenuForm(menu
      ? {
        entree: menu.entree || '',
        plat: menu.plat || '',
        dessert: menu.dessert || '',
        observations: menu.observations || ''
      }
      : MENU_VIDE);
    setShowMenuModal(true);
  };

  const handleSaveMenu = async () => {
    if (!menuDate) return;
    try {
      setIsSavingMenu(true);
      const response = await api.cantine.saveMenu({
        annee_scolaire_id: anneeScolaireId,
        date_service: formatDateForAPI(menuDate),
        entree: menuForm.entree,
        plat: menuForm.plat,
        dessert: menuForm.dessert,
        observations: menuForm.observations
      });

      if (response.success) {
        showToast('Menu enregistré.', 'success');
        setShowMenuModal(false);
        await loadMenus();
        // La feuille de pointage affiche le menu : on la rafraichit si c'est le meme jour.
        if (formatDateForAPI(menuDate) === feuille?.date_service) {
          await loadFeuilleDuJour();
        }
      } else {
        showToast(response.message || "Erreur lors de l'enregistrement du menu.", 'error');
      }
    } catch (error) {
      console.error('Erreur enregistrement menu cantine:', error);
      showToast("Erreur lors de l'enregistrement du menu.", 'error');
    } finally {
      setIsSavingMenu(false);
    }
  };

  const confirmerSuppressionMenu = async () => {
    if (!confirmSuppressionMenu) return;
    try {
      setIsSavingMenu(true);
      const response = await api.cantine.deleteMenu(confirmSuppressionMenu.menu.id, {
        annee_scolaire_id: anneeScolaireId
      });

      if (response.success) {
        showToast('Menu supprimé.', 'success');
        await loadMenus();
        if (formatDateForAPI(confirmSuppressionMenu.date) === feuille?.date_service) {
          await loadFeuilleDuJour();
        }
      } else {
        showToast(response.message || 'Erreur lors de la suppression du menu.', 'error');
      }
    } catch (error) {
      console.error('Erreur suppression menu cantine:', error);
      showToast('Erreur lors de la suppression du menu.', 'error');
    } finally {
      setIsSavingMenu(false);
      setConfirmSuppressionMenu(null);
    }
  };

  // ==========================================================================
  // TABLEAU DE BORD
  // ==========================================================================
  const bornesPeriode = useMemo(() => {
    const fin = new Date();
    const debut = new Date();

    if (periodeTdb === 'semaine') {
      debut.setTime(getLundiDeLaSemaine(new Date()).getTime());
    } else if (periodeTdb === 'trimestre') {
      debut.setMonth(debut.getMonth() - 3);
    } else {
      debut.setDate(1);
    }

    return { date_debut: formatDateForAPI(debut), date_fin: formatDateForAPI(fin) };
  }, [periodeTdb]);

  const loadTableauBord = useCallback(async () => {
    if (!anneeScolaireId) return;
    try {
      setLoadingTableauBord(true);
      const response = await api.cantine.getTableauDeBord({
        annee_scolaire_id: anneeScolaireId,
        ...bornesPeriode
      });
      if (response.success) {
        setTableauBord(response.data);
      } else {
        showToast(response.message || 'Erreur lors du chargement du tableau de bord.', 'error');
      }
    } catch (error) {
      console.error('Erreur chargement tableau de bord cantine:', error);
      showToast('Erreur lors du chargement du tableau de bord.', 'error');
    } finally {
      setLoadingTableauBord(false);
    }
  }, [anneeScolaireId, bornesPeriode]);

  useEffect(() => {
    if (anneeScolaireId) loadTableauBord();
  }, [anneeScolaireId, loadTableauBord]);

  // ==========================================================================
  // CREANCES ET SYNCHRONISATION
  // ==========================================================================
  const loadCreances = useCallback(async () => {
    if (!anneeScolaireId) return;
    try {
      setLoadingCreances(true);
      const response = await api.cantine.getRepasNonCouverts({ annee_scolaire_id: anneeScolaireId });
      if (response.success) {
        setCreances(response.data);
      } else {
        showToast(response.message || 'Erreur lors du chargement des créances.', 'error');
      }
    } catch (error) {
      console.error('Erreur chargement créances cantine:', error);
      showToast('Erreur lors du chargement des créances.', 'error');
    } finally {
      setLoadingCreances(false);
    }
  }, [anneeScolaireId]);

  useEffect(() => {
    if (anneeScolaireId) loadCreances();
  }, [anneeScolaireId, loadCreances]);

  const handleSynchroniserInscriptions = async () => {
    try {
      setIsSyncingInscriptions(true);
      const response = await api.cantine.synchroniserInscriptions({ annee_scolaire_id: anneeScolaireId });
      if (response.success) {
        showToast(response.message || 'Synchronisation effectuée.', 'success');
        await loadInscriptions();
        await loadFeuilleDuJour();
      } else {
        showToast(response.message || 'Erreur lors de la synchronisation.', 'error');
      }
    } catch (error) {
      console.error('Erreur synchronisation inscriptions cantine:', error);
      showToast('Erreur lors de la synchronisation des inscriptions.', 'error');
    } finally {
      setIsSyncingInscriptions(false);
    }
  };

  const elevesDejaInscrits = useMemo(
    () => inscriptions.map((i) => i.eleve_id),
    [inscriptions]
  );

  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="w-full space-y-6">

        <CantineHeader
          currentUser={currentUser}
          joursService={joursService}
          autoriserRepasNonPaye={autoriserRepasNonPaye}
        />

        {loadingParametres ? (
          <div className="px-6">
            <div className="flex min-h-[300px] items-center justify-center">
              <LoadingSpinner message="Chargement de la cantine..." color="#246990" />
            </div>
          </div>
        ) : (
          <div className="px-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 bg-gray-100">
                <TabsTrigger
                  value="pointage"
                  className="flex items-center gap-2 data-[state=active]:bg-[#246990] data-[state=active]:text-white"
                >
                  <ClipboardCheck size={16} />
                  Pointage du jour
                </TabsTrigger>
                <TabsTrigger
                  value="inscriptions"
                  className="flex items-center gap-2 data-[state=active]:bg-[#246990] data-[state=active]:text-white"
                >
                  <Users size={16} />
                  Inscriptions {resumeInscriptions.total > 0 && `(${resumeInscriptions.total})`}
                </TabsTrigger>
                <TabsTrigger
                  value="menus"
                  className="flex items-center gap-2 data-[state=active]:bg-[#246990] data-[state=active]:text-white"
                >
                  <UtensilsCrossed size={16} />
                  Menus
                </TabsTrigger>
                <TabsTrigger
                  value="tableau-de-bord"
                  className="flex items-center gap-2 data-[state=active]:bg-[#246990] data-[state=active]:text-white"
                >
                  <BarChart3 size={16} />
                  Tableau de bord
                </TabsTrigger>
                <TabsTrigger
                  value="creances"
                  className="flex items-center gap-2 data-[state=active]:bg-[#246990] data-[state=active]:text-white"
                >
                  <AlertTriangle size={16} />
                  Créances {creances?.resume?.nombre > 0 && `(${creances.resume.nombre})`}
                </TabsTrigger>
                <TabsTrigger
                  value="parametres"
                  className="flex items-center gap-2 data-[state=active]:bg-[#246990] data-[state=active]:text-white"
                >
                  <Settings size={16} />
                  Paramètres
                </TabsTrigger>
              </TabsList>

              <TabsContent value="pointage" className="mt-4">
                <CantinePointageSection
                  isLoading={loadingFeuille}
                  isSaving={isSavingPointage}
                  selectedDate={selectedDate}
                  onPreviousDay={goToPreviousDay}
                  onNextDay={goToNextDay}
                  onToday={goToToday}
                  onDateChange={handleDateChange}
                  feuille={feuille}
                  sections={sections}
                  classes={classes}
                  filterSection={filterSectionPointage}
                  onSectionChange={handleSectionPointageChange}
                  filterClasse={filterClassePointage}
                  setFilterClasse={setFilterClassePointage}
                  onPointer={handlePointer}
                  onAnnulerPointage={handleAnnulerPointage}
                />
              </TabsContent>

              <TabsContent value="inscriptions" className="mt-4">
                <CantineInscriptionsSection
                  isLoading={loadingInscriptions}
                  resume={resumeInscriptions}
                  inscriptions={inscriptions}
                  sections={sections}
                  classes={classes}
                  filterStatut={filterStatut}
                  setFilterStatut={setFilterStatut}
                  filterRegime={filterRegime}
                  setFilterRegime={setFilterRegime}
                  filterSection={filterSection}
                  onSectionChange={handleSectionChange}
                  filterClasse={filterClasse}
                  setFilterClasse={setFilterClasse}
                  search={search}
                  setSearch={setSearch}
                  onNouvelleInscription={ouvrirNouvelleInscription}
                  onModifierInscription={ouvrirModificationInscription}
                  onSupprimerInscription={setConfirmSuppression}
                  onGererRestrictions={ouvrirRestrictions}
                />
              </TabsContent>

              <TabsContent value="menus" className="mt-4">
                <CantineMenusSection
                  isLoading={loadingMenus}
                  lundi={lundiSemaine}
                  menusParDate={menusParDate}
                  joursService={joursService}
                  onPreviousWeek={goToPreviousWeek}
                  onNextWeek={goToNextWeek}
                  onCurrentWeek={goToCurrentWeek}
                  onEditMenu={ouvrirEditionMenu}
                  onDeleteMenu={(date, menu) => setConfirmSuppressionMenu({ date, menu })}
                />
              </TabsContent>

              <TabsContent value="tableau-de-bord" className="mt-4">
                <CantineTableauBordSection
                  isLoading={loadingTableauBord}
                  data={tableauBord}
                  periode={periodeTdb}
                  setPeriode={setPeriodeTdb}
                  onRefresh={loadTableauBord}
                />
              </TabsContent>

              <TabsContent value="creances" className="mt-4">
                <CantineCreancesSection
                  isLoading={loadingCreances}
                  data={creances}
                  autoriserRepasNonPaye={autoriserRepasNonPaye}
                  isSyncing={isSyncingInscriptions}
                  onSynchroniser={handleSynchroniserInscriptions}
                  onRefresh={loadCreances}
                />
              </TabsContent>

              <TabsContent value="parametres" className="mt-4">
                <div className="-mx-6">
                  <CantineParametresSection
                    isExpanded={expandedSections.has('parametres')}
                    onToggleSection={() => toggleSection('parametres')}
                    joursService={joursService}
                    onToggleJour={toggleJour}
                    autoriserRepasNonPaye={autoriserRepasNonPaye}
                    setAutoriserRepasNonPaye={setAutoriserRepasNonPaye}
                    observations={observations}
                    setObservations={setObservations}
                    isSaving={isSavingParametres}
                    isDirty={isParametresDirty}
                    onSave={handleSaveParametres}
                  />
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>

      <ModalInscriptionCantine
        isOpen={showInscriptionModal}
        onClose={() => setShowInscriptionModal(false)}
        mode={inscriptionMode}
        value={inscriptionForm}
        setValue={setInscriptionForm}
        eleves={eleves}
        elevesDejaInscrits={elevesDejaInscrits}
        isSaving={isSavingInscription}
        onSubmit={handleSaveInscription}
      />

      <ModalRestrictionsEleve
        isOpen={showRestrictionsModal}
        onClose={() => setShowRestrictionsModal(false)}
        eleve={eleveRestrictions}
        restrictions={restrictions}
        isLoading={loadingRestrictions}
        isSaving={isSavingRestriction}
        onAjouter={handleAjouterRestriction}
        onSupprimer={handleSupprimerRestriction}
      />

      <ModalMenuJour
        isOpen={showMenuModal}
        onClose={() => setShowMenuModal(false)}
        date={menuDate}
        value={menuForm}
        setValue={setMenuForm}
        isSaving={isSavingMenu}
        onSubmit={handleSaveMenu}
      />

      <ConfirmDialog
        isOpen={!!confirmSuppressionMenu}
        onClose={() => setConfirmSuppressionMenu(null)}
        onConfirm={confirmerSuppressionMenu}
        title="Supprimer le menu"
        description="Voulez-vous supprimer le menu de cette journée ? La feuille de pointage n'affichera plus aucun menu pour cette date."
        confirmText="Supprimer"
        cancelText="Annuler"
        type="warning"
        isLoading={isSavingMenu}
      />

      <ConfirmDialog
        isOpen={!!confirmSuppression}
        onClose={() => setConfirmSuppression(null)}
        onConfirm={confirmerSuppressionInscription}
        title="Retirer de la cantine"
        description={
          confirmSuppression
            ? `Voulez-vous retirer ${confirmSuppression.prenom} ${confirmSuppression.nom} de la cantine ? Les paiements déjà enregistrés ne sont pas affectés.`
            : ''
        }
        confirmText="Retirer"
        cancelText="Annuler"
        type="warning"
        isLoading={isSavingInscription}
      />

      {toastInfo && (
        shouldUseLongToast(toastInfo.message, toastInfo.type, toastInfo) ? (
          <LongToast show title={toastInfo.title} message={toastInfo.message} type={toastInfo.type} onClose={hideToast} />
        ) : (
          <Toast message={toastInfo.message} type={toastInfo.type} onClose={hideToast} />
        )
      )}
    </div>
  );
};

export default CantinePage;
